// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EmptyState } from '@/components/empty-state';
import { SupportAlert } from '@/components/support-alert';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import { ArrowOutwardRounded } from '@/constants/hardfin-standard-icons';
import { Sort } from '@/constants/sort';
import { ProjectSortProperty } from '@/project/api/get-projects';
import { Project } from '@/project/api/types';
import { ProjectTable } from '@/project/components/project-table';
import { ProjectTableSkeleton } from '@/project/components/project-table-skeleton';
import { Box, Paper, Stack, SxProps, Typography } from '@mui/material';

interface Props {
  /**
   * A collection of projects to display in the table. `null` while the current
   * query key is loading.
   */
  projects: Project[] | null;

  /**
   * Total count of projects for pagination. Null until the current query key
   * resolves so shared footer pagination can skip loading-specific UI.
   */
  totalCount: number | null;

  /**
   * When true, show a loading skeleton instead of actual data.
   */
  isLoading?: boolean;

  /**
   * The current sort state of the table.
   */
  sort: Sort<ProjectSortProperty>;

  /**
   * A callback fired when a sort header is clicked.
   */
  onSort: (property: ProjectSortProperty) => void;

  /**
   * A callback fired when the page is changed.
   */
  onPageChange?: (page: number) => void;

  /**
   * Current page number.
   */
  currentPage?: number;

  /**
   * Number of items per page.
   */
  pageSize?: number;

  /**
   * The style props of the table.
   */
  sx?: SxProps;
}

/**
 * A view component that displays a table of projects from the remote API.
 */
export function ProjectTableView(props: Props) {
  const {
    projects,
    totalCount,
    isLoading = false,
    currentPage = 1,
    pageSize = 50,
  } = props;

  const downloadURL = new URL(
    '/v1/export/projects',
    import.meta.env.VITE_API_URL
  );

  if (isLoading) {
    return <ProjectTableSkeleton />;
  }

  if (projects === null) {
    return <SupportAlert message="An error occurred while loading projects." />;
  }

  const resolvedTotalCount = totalCount ?? projects.length;
  const pagination = props.onPageChange
    ? {
        page: currentPage,
        pageSize,
        onPageChange: props.onPageChange,
      }
    : undefined;

  if (projects.length > 0) {
    return (
      <>
        <Paper variant="outlined" component={Box} overflow="hidden">
          <ProjectTable
            projects={projects}
            sort={props.sort}
            onSort={props.onSort}
            sx={props?.sx}
          />
        </Paper>
        <TablePaginationFooter
          pagination={pagination}
          totalCount={resolvedTotalCount}
          entityOptions={{ singular: 'project', includeCount: false }}
          downloadURL={downloadURL}
          downloadLabel="Export project data"
        />
      </>
    );
  }

  return (
    <EmptyState title="No projects found">
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography color="text.secondary">
          To start tracking projects in Hardfin, add a new project
        </Typography>
        <ArrowOutwardRounded fontSize="small" color="action" />
      </Stack>
    </EmptyState>
  );
}
